import React, { type DetailedHTMLProps, type InputHTMLAttributes } from 'react';

export type TextFieldProps = Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'onChange' | 'size'
> & {
  label?: string | React.ReactNode;
  error?: string | React.ReactNode;
  onChange: (value: string) => void;
  forwardRef?: React.ForwardedRef<HTMLInputElement>;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  formGroupStyle?: React.CSSProperties;
};

const TheTextField: React.FC<TextFieldProps> = ({
  onChange,
  label,
  error,
  forwardRef,
  leadingIcon,
  trailingIcon,
  style,
  size,
  formGroupStyle,
  ...rest
}) => {
  const styleImpl = React.useMemo(() => {
    const styleImpl = { ...style };
    if (leadingIcon) styleImpl.paddingLeft = 30;
    if (trailingIcon) styleImpl.paddingRight = 30;
    return styleImpl;
  }, [style, leadingIcon, trailingIcon]);

  return (
    <div className={`ui-form-group ${error ? 'ui-has-error' : ''}`} style={formGroupStyle}>
      {label && <label htmlFor={rest.id}>{label}</label>}
      <div style={{ position: 'relative' }}>
        {leadingIcon && (
          <div
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            {leadingIcon}
          </div>
        )}
        <input
          className={`ui-text-field ui-text-field-${size ?? 'default'}`}
          onChange={(e) => onChange(e.currentTarget.value)}
          autoComplete="new"
          ref={forwardRef}
          style={styleImpl}
          {...rest}
        />
        {trailingIcon && (
          <div
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            {trailingIcon}
          </div>
        )}
      </div>

      {error && <div className="ui-error">{error}</div>}
    </div>
  );
};

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => (
  <TheTextField forwardRef={ref} {...props} />
));
